import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Form, InputGroup, Badge, Spinner, Pagination } from 'react-bootstrap';
import { LuSearch, LuFilter, LuPlus, LuMinus, LuShoppingCart, LuBox } from "react-icons/lu";
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import { addToCart, updateCartItem } from '../../redux/reducers/portalCartSlice';
import { formatCurrency } from '../../utils/currency';
import { useNavigate, useParams } from 'react-router-dom';

const PortalProducts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businessId } = useParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, pages: 1 });
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [categories, setCategories] = useState([]);
    
    const { cart } = useSelector((state) => state.portalCart);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');


    const fetchProducts = async (page = 1) => {
        setLoading(true);
        try {
            const res = await PortalAxios.PortalRequest({
                method: 'get',
                url: `${baseURL}/portal/products?page=${page}&limit=12&search=${search}&category=${category}`
            });
            setProducts(res.data.data.list);
            setPagination(res.data.data.pagination);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            // Reusing the business endpoint but we don't have businessId here easily without selector
            // Actually portalAuth has it
        } catch (err) {}
    };

    useEffect(() => {
        fetchProducts();
    }, [search, category]);

    const handleAddToCart = (productId) => {
        dispatch(addToCart({ productId, quantity: 1 }));
    };

    const getItemInCart = (productId) => {
        return cart?.items?.find(i => i.productId === productId);
    };

    return (
        <div>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
                <div>
                    <h2 className="fw-bold text-dark mb-1">Product Catalog</h2>
                    <p className="text-muted mb-0">Browse and order items from our store.</p>
                </div>
                
                <div className="d-flex gap-2">
                    <InputGroup className="shadow-sm rounded-3 overflow-hidden" style={{ minWidth: '250px' }}>
                        <InputGroup.Text className="bg-white border-0"><LuSearch className="text-muted" /></InputGroup.Text>
                        <Form.Control 
                            placeholder="Search products..." 
                            className="border-0 py-2 shadow-none"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>
                </div>
            </div>

            <Row className="g-4">
                {loading ? (
                    <Col xs={12} className="text-center py-5">
                        <Spinner animation="border" style={{ color: "#28B0C4" }} />
                    </Col>
                ) : products.length > 0 ? (
                    products.map((product) => {
                        const inCart = getItemInCart(product._id);
                        return (
                            <Col key={product._id} xs={6} md={4} lg={3}>
                                <Card className="product-card border-0 shadow-sm rounded-4 h-100 overflow-hidden d-flex flex-column">
                                    <div 
                                        className="bg-light d-flex align-items-center justify-content-center cursor-pointer position-relative overflow-hidden" 
                                        style={{ height: '200px' }}
                                        onClick={() => navigate(`/customer-side/${businessId}/products/${product._id}`)}
                                    >
                                        {product.images?.[0] ? (
                                            <img 
                                                src={product.images[0]} 
                                                alt={product.name} 
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }} 
                                            />
                                        ) : (
                                            <LuBox size={48} className="text-muted opacity-25" />
                                        )}
                                        {product.productCategory && (
                                            <Badge bg="white" className="position-absolute top-0 end-0 m-3 text-dark shadow-sm fw-semibold rounded-pill">
                                                {product.productCategory.name}
                                            </Badge>
                                        )}
                                    </div>
                                    <Card.Body className="p-3 d-flex flex-column">
                                        <h6 
                                            className="fw-bold text-dark mb-1 cursor-pointer text-truncate" 
                                            title={product.name}
                                            onClick={() => navigate(`/customer-side/${businessId}/products/${product._id}`)}
                                        >
                                            {product.name}
                                        </h6>
                                        <div className="fs-5 fw-bold mb-3" style={{ color: "#28B0C4" }}>
                                            {product.pricingTiers?.length > 0 ? (
                                                <>
                                                    <small className="text-muted fs-6 fw-normal me-1">From</small>
                                                    {formatCurrency(Math.min(product.price, ...product.pricingTiers.map(t => t.price)), currency)}
                                                </>
                                            ) : (
                                                formatCurrency(product.price, currency)
                                            )}
                                        </div>
                                        
                                        <div className="mt-auto">
                                            {inCart ? (
                                                <div className="d-flex align-items-center justify-content-between bg-light rounded-3 p-1">
                                                    <Button 
                                                        variant="white" 
                                                        size="sm" 
                                                        className="rounded-2 shadow-sm border-0 p-1"
                                                        onClick={() => dispatch(updateCartItem({ productId: product._id, quantity: inCart.quantity - 1 }))}
                                                    >
                                                        <LuMinus size={14} />
                                                    </Button>
                                                    <span className="fw-bold px-2">{inCart.quantity}</span>
                                                    <Button 
                                                        variant="white" 
                                                        size="sm" 
                                                        className="rounded-2 shadow-sm border-0 p-1"
                                                        onClick={() => dispatch(updateCartItem({ productId: product._id, quantity: inCart.quantity + 1 }))}
                                                    >
                                                        <LuPlus size={14} />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button 
                                                    className="w-100 rounded-3 py-2 fw-bold text-white border-0 d-flex align-items-center justify-content-center gap-2"
                                                    style={{ background: "#28B0C4" }}
                                                    onClick={() => handleAddToCart(product._id)}
                                                    disabled={product.quantity <= 0 && product.productType === 'item'}
                                                >
                                                    <LuPlus size={16} /> 
                                                    {product.quantity <= 0 && product.productType === 'item' ? 'Out of Stock' : 'Add to Cart'}
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })
                ) : (
                    <Col xs={12} className="text-center py-5 text-muted">
                        No products found matching your search.
                    </Col>
                )}
            </Row>

            {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                    <Pagination>
                        <Pagination.Prev disabled={pagination.page === 1} onClick={() => fetchProducts(pagination.page - 1)} />
                        {[...Array(pagination.pages)].map((_, i) => (
                            <Pagination.Item 
                                key={i + 1} 
                                active={i + 1 === pagination.page}
                                onClick={() => fetchProducts(i + 1)}
                                style={{ '--bs-pagination-active-bg': '#28B0C4', '--bs-pagination-active-border-color': '#28B0C4' }}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={pagination.page === pagination.pages} onClick={() => fetchProducts(pagination.page + 1)} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default PortalProducts;
